import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radar, Sparkles, Home, Flame, Mail, MessageCircle, Calendar, Users, Handshake,
  Rocket, Sun, Moon, Play, Pause, RotateCcw, X, Music, Zap, CheckCircle2, Clock,
  Eye, MousePointerClick, ArrowRight, Volume2, VolumeX,
} from "lucide-react";

const GOLD = "#C19A6B";

// --- Plans cinématographiques (biens Oise générés + vraies photos de Vincent Marette) ---
const IMG = {
  // Vraies photos de Vincent Marette (déjà hébergées sur Off Markette)
  vincentCar: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/m0jq5786_WhatsApp%20Image%202026-07-08%20at%2012.50.38.jpeg",
  vincentPhone: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/sm8h96mw_WhatsApp%20Image%202026-07-08%20at%2012.50.26.jpeg",
  vincentStudio: "https://customer-assets.emergentagent.com/job_appart-jardin/artifacts/it273fo1_WhatsApp%20Image%202026-07-08%20at%2012.48.15%20%281%29.jpeg",
  vincentVendu: "https://customer-assets-4nw71qhi.emergentagent.net/job_appart-jardin/artifacts/348xqlss_WhatsApp%20Image%202026-07-08%20at%2012.51.16.jpeg",
  // Biens style Oise (Chantilly / Senlis)
  house: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/dac2b28d3df2b0f3a33a76790098d2f7955bbb5c35285915900236cea06a673f.jpeg",
  senlis: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/9cb4495ec92737c5c353f21638105985070a2e00ac29e1758f868e713e3f4714.jpeg",
  night: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/ac0a7e0e712efb57c20adb32249c614fc6be22a155aacc3af1a0fbfd9aad2c1f.jpeg",
  // Intérieurs Home Staging
  before: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/eb9810dc72a19bd54068b76c8ce73749d09ccb3a70adac19b51e04eef842ae84.jpeg",
  after: "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/3bcf669ad46676a1c9944916ba757d9ecfaead152998a4a9b1e813121579d506.jpeg",
};

// --- Jeu de données DÉMO dédié (séparé de la production) ---
const DEMO = {
  advisor: "Vincent Marette",
  listing: { title: "Maison de charme à Chantilly", price: "750 000 €", city: "Chantilly", rooms: "5 chambres", extra: ["Piscine", "Grand jardin"] },
  prospect: { name: "Julien P.", score: 94, search: "Maison · Chantilly / Senlis · 4 ch. min · Piscine", budget: "700 – 800 k€", source: "iad" },
  partner: { name: "Patrick O.", via: "Century 21", score: 94 },
  boost: { title: "Maison Senlis", scanned: 83, qualified: 12, over90: 6 },
};

const RADAR_CH = [
  { k: "acq", label: "Vos acquéreurs", on: true }, { k: "part", label: "Partenaires", on: true },
  { k: "om", label: "Off Markette", on: true }, { k: "crm", label: "CRM", on: true },
  { k: "web", label: "Web", on: true }, { k: "email", label: "Email", on: true },
  { k: "fb", label: "Facebook", on: false }, { k: "wa", label: "WhatsApp", on: false },
  { k: "ig", label: "Instagram", on: false },
];

const fade = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const rise = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 } };

// ---------- Primitives de scène ----------
function TimeBadge({ t }) {
  return <div className="absolute top-4 left-4 z-20 bg-black/60 backdrop-blur px-3 py-1 rounded-full text-white text-xs font-bold tracking-widest">{t}</div>;
}
function RealShot({ img, time, overlay, pos = "center" }) {
  return (
    <div className="absolute inset-0">
      <motion.img src={img} alt="" style={{ objectPosition: pos }} className="w-full h-full object-cover" initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: "linear" }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/40" />
      {time && <TimeBadge t={time} />}
      {overlay}
    </div>
  );
}
function ScreenFrame({ children, label }) {
  return (
    <motion.div {...rise} transition={{ duration: 0.5 }} className="relative w-full max-w-md mx-auto">
      <div className="rounded-2xl bg-[#F8F9FA] border border-white/10 shadow-2xl overflow-hidden ring-1 ring-black/5">
        <div className="h-7 bg-[#0A0A0A] flex items-center gap-1.5 px-3">
          <span className="w-2 h-2 rounded-full bg-white/20" /><span className="w-2 h-2 rounded-full bg-white/20" /><span className="w-2 h-2 rounded-full bg-white/20" />
          {label && <span className="ml-2 text-[10px] text-zinc-400 tracking-wide">{label}</span>}
        </div>
        <div className="p-4">{children}</div>
      </div>
    </motion.div>
  );
}
function Count({ to, dur = 1.2, suffix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (ts) => { if (!start) start = ts; const p = Math.min(1, (ts - start) / (dur * 1000)); setN(Math.round(p * to)); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step); return () => cancelAnimationFrame(raf);
  }, [to, dur]);
  return <>{n}{suffix}</>;
}

// ---------- Écrans (interfaces Off Markette) ----------
export const EmmaActive = () => (
  <ScreenFrame label="Mon Agent IA">
    <div className="flex items-center gap-3">
      <motion.div className="w-14 h-14 rounded-full bg-[#C19A6B]/15 flex items-center justify-center relative" initial={{ scale: 0.8 }} animate={{ scale: 1 }}>
        {[0, 1].map((i) => <motion.span key={i} className="absolute inset-0 rounded-full border border-[#C19A6B]/50" animate={{ scale: [1, 1.5], opacity: [0.6, 0] }} transition={{ duration: 2, repeat: Infinity, delay: i }} />)}
        <Sparkles className="w-6 h-6 text-[#C19A6B]" />
      </motion.div>
      <div>
        <p className="text-lg font-bold text-[#111827]">Emma</p>
        <p className="text-xs font-semibold text-emerald-600 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> ACTIVE</p>
      </div>
    </div>
    <div className="mt-4 rounded-xl bg-[#111827] p-3 flex items-center gap-2">
      <Radar className="w-4 h-4 text-[#C19A6B]" /><span className="text-white text-xs font-semibold">Radar Acquéreurs IA</span>
      <span className="ml-auto text-[10px] text-emerald-400 font-bold">ACTIF</span>
    </div>
    <div className="mt-2 flex items-center gap-2">
      <img src={IMG.vincentStudio} alt="Vincent Marette" className="w-7 h-7 rounded-full object-cover object-top" />
      <span className="text-[11px] text-zinc-500">Au service de <b className="text-[#111827]">Vincent Marette</b></span>
    </div>
  </ScreenFrame>
);

export const RadarShot = () => (
  <div className="relative w-full max-w-md mx-auto flex items-center justify-center py-4">
    <div className="relative w-64 h-64">
      {[0, 1, 2].map((i) => <motion.span key={i} className="absolute inset-0 rounded-full border border-[#C19A6B]/40" animate={{ scale: [1, 1.35, 1], opacity: [0.5, 0, 0.5] }} transition={{ duration: 3, repeat: Infinity, delay: i }} />)}
      <motion.div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(from 0deg, transparent 0deg, ${GOLD}55 40deg, transparent 90deg)` }} animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} />
      <div className="absolute inset-0 flex items-center justify-center"><div className="w-16 h-16 rounded-full bg-[#C19A6B] flex items-center justify-center shadow-xl"><Radar className="w-8 h-8 text-white" /></div></div>
      {RADAR_CH.map((c, i) => { const a = (i / RADAR_CH.length) * Math.PI * 2; const r = 120; return (
        <motion.div key={c.k} className={`absolute text-[8px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${c.on ? "bg-emerald-500/90 text-white" : "bg-white/15 text-zinc-300 border border-white/20"}`}
          style={{ left: `calc(50% + ${Math.cos(a) * r}px)`, top: `calc(50% + ${Math.sin(a) * r}px)`, transform: "translate(-50%,-50%)" }}
          initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 * i }}>{c.label}</motion.div>
      ); })}
    </div>
  </div>
);

const ListingsScan = () => (
  <ScreenFrame label="Mes biens">
    <div className="flex items-center justify-between mb-2"><p className="text-xs font-semibold text-zinc-500">Emma analyse vos mandats…</p><span className="text-[#C19A6B] font-extrabold text-lg"><Count to={17} /> biens</span></div>
    <div className="space-y-1.5">
      {["Maison Chantilly · 750 000 €", "Appartement Senlis · 245 000 €", "Maison Compiègne · 410 000 €"].map((t, i) => (
        <motion.div key={t} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 * i }} className={`flex items-center gap-2 rounded-lg border p-2 ${i === 0 ? "border-[#C19A6B]/50 bg-[#C19A6B]/5" : "border-zinc-100 bg-white"}`}>
          <Home className="w-4 h-4 text-[#C19A6B]" /><span className="text-xs text-[#111827] font-medium">{t}</span>
          <motion.span className="ml-auto w-3.5 h-3.5 rounded-full border-2 border-[#C19A6B] border-t-transparent" animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} />
        </motion.div>
      ))}
    </div>
  </ScreenFrame>
);

export const SignalShot = () => (
  <ScreenFrame label="Radar · nouveau signal">
    <div className="flex items-center gap-2 mb-2 text-[#C19A6B]"><Sparkles className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">Nouveau signal d'achat</span></div>
    <div className="rounded-xl bg-[#FDFBF7] border border-[#E8D8C3] p-3 text-[13px] text-[#111827] space-y-1">
      <p className="font-semibold">Recherche détectée</p>
      <p className="text-zinc-600">{DEMO.prospect.search}</p>
      <p className="text-zinc-600">Budget {DEMO.prospect.budget}</p>
      <p className="text-[11px] text-zinc-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Source : {DEMO.prospect.source}</p>
    </div>
  </ScreenFrame>
);

export const MatchShot = ({ house = IMG.house, title = "Maison Chantilly", score = 96 } = {}) => (
  <div className="relative w-full max-w-md mx-auto">
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
      <motion.div {...rise} className="rounded-xl bg-white border border-zinc-100 p-3 text-center shadow">
        <img src={house} alt="" className="w-full h-16 object-cover rounded-lg mb-1" />
        <p className="text-[11px] font-bold text-[#111827] leading-tight">{title}</p>
      </motion.div>
      <div className="text-center">
        <motion.div className="text-3xl font-extrabold text-[#C19A6B]" initial={{ scale: 0.6 }} animate={{ scale: [0.6, 1.2, 1] }} transition={{ duration: 1.4 }}>
          {score}%
        </motion.div>
        <p className="text-[10px] font-bold tracking-widest text-[#111827]">MATCH</p>
        <Flame className="w-5 h-5 text-[#EF6C3B] mx-auto mt-1" />
      </div>
      <motion.div {...rise} className="rounded-xl bg-white border border-zinc-100 p-3 text-center shadow">
        <div className="w-full h-16 rounded-lg bg-[#C19A6B]/12 flex items-center justify-center mb-1"><span className="text-lg font-bold text-[#C19A6B]">JP</span></div>
        <p className="text-[11px] font-bold text-[#111827] leading-tight">{DEMO.prospect.name}</p>
      </motion.div>
    </div>
  </div>
);

export const MiniFiche = () => (
  <ScreenFrame label="Fiche prospect">
    <div className="flex items-center gap-2.5">
      <div className="w-10 h-10 rounded-full bg-[#C19A6B]/12 flex items-center justify-center text-[#C19A6B] font-bold text-sm">JP</div>
      <div className="flex-1"><p className="text-sm font-bold text-[#111827]">{DEMO.prospect.name}</p><p className="text-[11px] text-zinc-500">{DEMO.prospect.search}</p></div>
      <span className="text-[#C19A6B] font-bold inline-flex items-center gap-0.5"><Flame className="w-3.5 h-3.5" />94%</span>
    </div>
    <div className="mt-3 rounded-xl border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-[#A88358] font-semibold mb-1">Prochaine action conseillée</p>
      <p className="text-[13px] text-[#111827] mb-2">🔥 Prospect chaud — proposez une visite via WhatsApp</p>
      <div className="inline-flex items-center gap-1.5 bg-[#C19A6B] text-white text-[11px] font-semibold rounded-lg px-3 py-1.5"><MessageCircle className="w-3.5 h-3.5" /> Préparer le message</div>
    </div>
  </ScreenFrame>
);

export const WhatsAppShot = () => (
  <div className="relative w-full max-w-[260px] mx-auto">
    <motion.div {...rise} className="rounded-[26px] bg-[#0A0A0A] p-2 shadow-2xl">
      <div className="rounded-[20px] bg-[#ECE5DD] overflow-hidden">
        <div className="bg-[#075E54] px-3 py-2 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-white" /><span className="text-white text-xs font-semibold">Julien P.</span></div>
        <div className="p-3 space-y-2">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-lg rounded-tl-none p-2 text-[11px] text-[#111827] shadow-sm">
            Bonjour Sébastien, un bien correspondant à votre recherche autour de Chantilly vient d'être identifié…
            <img src={IMG.house} alt="" className="w-full h-16 object-cover rounded mt-1.5" />
            <p className="text-[10px] text-[#C19A6B] mt-1">off-markette.com/bien/…</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="flex justify-center">
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Message prêt — validez l'envoi</span>
          </motion.div>
        </div>
      </div>
    </motion.div>
  </div>
);

// Home staging de LA maison visitee (meme salon du chateau) : avant = photo reelle,
// apres = home staging contemporain genere sur le MEME point de vue (superposable).
const STAGE_BEFORE = "https://appart-jardin.preview.emergentagent.com/api/media/assets/story_staging_room_531b4c2a0b37.jpeg";
const STAGE_AFTER = "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/57a86190753616c6c26f99a9c41c93598736becc2006a56e9f6d43ed38781a99.jpeg";

export const EmailStaging = () => (
  <ScreenFrame label="Home Staging IA">
    <p className="text-xs font-semibold text-zinc-500 mb-2">La maison visitée, en version contemporaine</p>
    <div className="grid grid-cols-2 gap-1.5">
      <div className="relative"><img src={STAGE_BEFORE} alt="" className="w-full h-20 object-cover rounded-lg" /><span className="absolute bottom-1 left-1 text-[9px] font-bold text-white bg-black/50 px-1.5 rounded">AVANT</span></div>
      <motion.div className="relative" initial={{ clipPath: "inset(0 100% 0 0)" }} animate={{ clipPath: "inset(0 0% 0 0)" }} transition={{ duration: 1.2, delay: 0.4 }}>
        <img src={STAGE_AFTER} alt="" className="w-full h-20 object-cover rounded-lg" /><span className="absolute bottom-1 left-1 text-[9px] font-bold text-white bg-[#C19A6B] px-1.5 rounded">APRÈS</span>
      </motion.div>
    </div>
    <p className="calli text-lg text-[#C19A6B] text-center mt-2">Imaginez le potentiel</p>
  </ScreenFrame>
);

export const CTAJoin = () => (
  <div className="relative w-full max-w-sm mx-auto">
    <motion.div {...rise} transition={{ duration: 0.5 }} className="rounded-2xl bg-[#0A0A0A] border border-[#C19A6B]/45 shadow-2xl overflow-hidden text-center px-6 py-6">
      <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#C19A6B]">Off Markette</p>
      <p className="calli text-3xl text-white mt-2 leading-tight">Rejoignez le réseau</p>
      <p className="text-[12px] text-white/70 mt-1.5">Agents, apporteurs — développez votre activité</p>
      <motion.div
        className="mt-4 inline-flex items-center gap-2 bg-[#C19A6B] text-[#0A0A0A] text-sm font-bold rounded-full px-6 py-2.5"
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      >
        Nous rejoindre <ArrowRight className="w-4 h-4" />
      </motion.div>
    </motion.div>
  </div>
);

export const PartnerShot = () => {
  const [added, setAdded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAdded(true), 2200); return () => clearTimeout(t); }, []);
  return (
    <ScreenFrame label="Match partenaire">
      <div className="flex items-center gap-2 text-[#C19A6B] mb-2"><Handshake className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">Match partenaire · 94%</span></div>
      <div className="rounded-xl border border-zinc-100 bg-white p-3">
        <p className="text-sm font-semibold text-[#111827]">{DEMO.partner.name} <span className="text-[11px] text-zinc-400 font-normal">· {DEMO.partner.via}</span></p>
        <p className="text-[11px] text-zinc-500 mt-0.5">Acquéreur compatible chez un conseiller du réseau</p>
        <AnimatePresence mode="wait">
          {!added ? (
            <motion.div key="btn" {...fade} className="mt-2 inline-flex items-center gap-1.5 bg-[#C19A6B] text-white text-[11px] font-semibold rounded-lg px-3 py-1.5"><Users className="w-3.5 h-3.5" /> + Ajouter à mon réseau</motion.div>
          ) : (
            <motion.div key="ok" {...rise} className="mt-2 text-[11px] font-bold text-emerald-700 bg-emerald-100 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> {DEMO.partner.name} ajouté à vos partenaires</motion.div>
          )}
        </AnimatePresence>
      </div>
    </ScreenFrame>
  );
};

const BoostShot = () => (
  <ScreenFrame label="Boost Mon Bien">
    <div className="flex items-center gap-2 mb-2"><Rocket className="w-4 h-4 text-[#C19A6B]" /><span className="text-xs font-bold text-[#111827]">{DEMO.boost.title}</span><span className="ml-auto text-[10px] text-[#C19A6B] font-semibold">⚡ Boosté</span></div>
    <p className="text-[11px] text-zinc-500 mb-2">Emma concentre sa prospection sur ce mandat.</p>
    <div className="grid grid-cols-3 gap-1.5">
      {[[DEMO.boost.scanned, "Signaux"], [DEMO.boost.qualified, "Qualifiés"], [DEMO.boost.over90, "Matchs >90%"]].map(([v, l]) => (
        <div key={l} className="rounded-lg bg-[#FDFBF7] border border-[#E8D8C3] p-2 text-center"><div className="text-lg font-extrabold text-[#C19A6B]"><Count to={v} /></div><div className="text-[9px] text-zinc-500">{l}</div></div>
      ))}
    </div>
    <p className="text-[9px] text-zinc-400 mt-2 text-center">Environnement démo — chiffres illustratifs</p>
  </ScreenFrame>
);

export const RecapShot = () => (
  <ScreenFrame label="Dashboard · retour">
    <div className="flex items-center gap-2 mb-1">
      <img src={IMG.vincentStudio} alt="Vincent Marette" className="w-8 h-8 rounded-full object-cover object-top" />
      <div className="flex items-center gap-2 text-[#C19A6B]"><Moon className="w-3.5 h-3.5" /><span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Bonsoir {DEMO.advisor.split(" ")[0]}</span></div>
    </div>
    <p className="text-sm font-bold text-[#111827] mb-3">Emma a travaillé pour vous.</p>
    <div className="grid grid-cols-3 gap-1.5">
      {[["126", "signaux"], ["14", "prospects"], ["7", "matchs >90%"], ["4", "contacts prêts"], ["2", "à suivre"], ["1", "visite"]].map(([v, l]) => (
        <div key={l} className="rounded-lg bg-white border border-zinc-100 p-2 text-center"><div className="text-base font-extrabold text-[#C19A6B]">{v}</div><div className="text-[9px] text-zinc-500 leading-tight">{l}</div></div>
      ))}
    </div>
    <div className="mt-2 text-center text-[10px] text-zinc-400">Environnement démo</div>
  </ScreenFrame>
);

export const TimelineShot = () => {
  const items = [["08:16", "17 biens analysés", Home], ["08:21", "Radar acquéreurs lancé", Radar], ["08:29", "Match 96 %", Flame], ["08:32", "Email préparé", Mail], ["09:11", "Home Staging consulté", Eye], ["10:02", "Match partenaire", Handshake], ["16:42", "Demande de visite", Calendar]];
  return (
    <ScreenFrame label="Le travail d'Emma">
      <div className="space-y-1.5 max-h-[220px] overflow-hidden">
        {items.map(([t, txt, Ic], i) => (
          <motion.div key={t} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 * i }} className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-zinc-400 w-9">{t}</span>
            <span className="w-5 h-5 rounded-full bg-[#C19A6B]/15 flex items-center justify-center"><Ic className="w-3 h-3 text-[#C19A6B]" /></span>
            <span className="text-[12px] text-[#111827]">{txt}</span>
          </motion.div>
        ))}
      </div>
    </ScreenFrame>
  );
};

// ---------- Scénario ----------
const SCENES = [
  { dur: 5, chapter: "La journée commence", sub: "Votre journée commence. Une visite, une estimation, un rendez-vous chez le notaire…", render: () => <RealShot img={IMG.vincentCar} pos="center 30%" time="8:12" overlay={<div className="absolute bottom-24 left-6 right-6 z-20"><div className="inline-flex flex-col gap-1">{["Visite — 9h00", "Estimation — 11h00", "Notaire — 14h30", "Visite — 17h00"].map((t, i) => <motion.span key={t} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.3 }} className="text-white text-sm font-semibold bg-black/60 backdrop-blur px-3 py-1 rounded-lg w-fit">{t}</motion.span>)}</div></div>} /> },
  { dur: 3, chapter: "La journée commence", sub: "Mais pendant que vous exercez votre métier… votre prospection peut continuer.", render: () => <div className="text-center px-8"><motion.p {...rise} className="text-white text-2xl sm:text-3xl font-extrabold">Et votre prospection ?</motion.p></div> },
  { dur: 4, chapter: "Emma entre en scène", sub: "Voici Emma, votre Agent IA Off Markette. Elle a déjà commencé.", render: () => <EmmaActive /> },
  { dur: 5, chapter: "Le Radar", sub: "Elle croise vos acquéreurs, ceux de vos partenaires et les sources accessibles.", render: () => <RadarShot /> },
  { dur: 5, chapter: "Analyse des biens", sub: "Elle analyse vos biens, un à un.", render: () => <ListingsScan /> },
  { dur: 4, chapter: "Un signal apparaît", sub: "Un nouveau signal d'achat vient d'être détecté.", render: () => <SignalShot /> },
  { dur: 5, chapter: "Le match", sub: "96 %. Un acquéreur potentiel vient d'être identifié.", render: () => <MatchShot /> },
  { dur: 5, chapter: "La mini-fiche", sub: "La fiche est prête — et l'action recommandée aussi.", render: () => <MiniFiche /> },
  { dur: 5, chapter: "WhatsApp", sub: "Emma prépare le bon message. Vous n'avez plus qu'à valider.", render: () => <WhatsAppShot /> },
  { dur: 5, chapter: "Email & Home Staging", sub: "Le bon bien, mis en valeur. Imaginez le potentiel.", render: () => <EmailStaging /> },
  { dur: 4, chapter: "Pendant ce temps", sub: "Pendant ce temps, vous faites votre métier.", render: () => <RealShot img={IMG.vincentPhone} pos="center 25%" overlay={<div className="absolute bottom-24 left-6 z-20"><motion.span {...fade} className="text-white text-sm font-semibold bg-black/60 px-3 py-1 rounded-lg">Pendant ce temps…</motion.span></div>} /> },
  { dur: 5, chapter: "Le réseau", sub: "Elle crée aussi des opportunités entre agents.", render: () => <PartnerShot /> },
  { dur: 5, chapter: "Boost Mon Bien", sub: "Un mandat mérite plus d'attention ? Boostez-le.", render: () => <BoostShot /> },
  { dur: 5, chapter: "Le retour", sub: "Et lorsque vous revenez… le travail vous attend déjà.", render: () => <RecapShot /> },
  { dur: 4, chapter: "Le résultat", sub: "Des visites. Des offres. Des ventes.", render: () => <RealShot img={IMG.vincentVendu} pos="center 22%" overlay={<div className="absolute bottom-24 left-6 right-6 z-20 text-center"><motion.span {...rise} className="inline-block text-white text-lg font-extrabold bg-[#C19A6B]/90 px-4 py-1.5 rounded-lg">Un mandat de plus… VENDU.</motion.span></div>} /> },
  { dur: 5, chapter: "Le travail d'Emma", sub: "Chaque étape, tracée pour vous.", render: () => <TimelineShot /> },
  { dur: 4, chapter: "La nuit", sub: "Selon vos moulinettes programmées, même la nuit, la recherche continue.", render: () => <RealShot img={IMG.night} time="23:42" overlay={<div className="absolute bottom-24 left-6 right-6 z-20 space-y-1">{["23:43 — Nouvelle moulinette", "23:46 — Recherche des opportunités", "23:49 — Nouveau match 94 %"].map((t, i) => <motion.p key={t} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.6 }} className="text-[#E8D8C3] text-xs font-mono">{t}</motion.p>)}</div>} /> },
  { dur: 5, chapter: "Signature", sub: "Même quand vous êtes en visite, chez le notaire… ou quand vous dormez.", render: () => <div className="text-center px-8"><motion.p {...rise} className="text-white text-xl sm:text-2xl font-bold leading-snug">Même quand vous êtes en visite,<br />chez le notaire…<br /><span className="text-[#C19A6B]">ou même quand vous dormez.</span></motion.p><motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="calli text-2xl sm:text-3xl text-[#C19A6B] mt-4">Votre prospection continue.</motion.p></div> },
];
const TOTAL = SCENES.reduce((a, s) => a + s.dur, 0);

// ---------- Lecteur plein écran ----------
export function AgentFilmPlayer({ onClose, onActivate }) {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [ended, setEnded] = useState(false);
  const [music, setMusic] = useState(() => { try { return localStorage.getItem("emma_film_music") || ""; } catch (_) { return ""; } });
  const [muted, setMuted] = useState(false);
  const [showMusic, setShowMusic] = useState(false);
  const audioRef = useRef(null);
  const raf = useRef(null); const last = useRef(null);

  useEffect(() => {
    const loop = (ts) => {
      if (last.current == null) last.current = ts;
      const dt = (ts - last.current) / 1000; last.current = ts;
      if (playing && !ended) setT((p) => { const n = p + dt; if (n >= TOTAL) { setEnded(true); setPlaying(false); return TOTAL; } return n; });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, ended]);

  useEffect(() => { last.current = null; }, [playing]);
  useEffect(() => { const a = audioRef.current; if (!a) return; a.muted = muted; if (playing && music && !ended) { a.play().catch(() => {}); } else { a.pause(); } }, [playing, music, muted, ended]);

  let acc = 0, idx = 0;
  for (let i = 0; i < SCENES.length; i++) { if (t < acc + SCENES[i].dur) { idx = i; break; } acc += SCENES[i].dur; idx = i; }
  const scene = SCENES[idx];
  const restart = () => { setT(0); setEnded(false); setPlaying(true); };
  const saveMusic = (url) => { setMusic(url); try { localStorage.setItem("emma_film_music", url); } catch (_) {} };

  return createPortal(
    <motion.div {...fade} className="fixed inset-0 z-[3000] bg-black flex flex-col" data-testid="agent-film-player">
      {/* Scène */}
      <div className="relative flex-1 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#141210] to-[#1a1712]" />
        <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-10">
          <AnimatePresence mode="wait">
            <motion.div key={idx} {...fade} transition={{ duration: 0.5 }} className="w-full flex items-center justify-center">
              {scene.render()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Chapitre */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 text-[11px] uppercase tracking-[0.25em] text-[#E4C79E] font-semibold [text-shadow:_0_1px_8px_rgba(0,0,0,0.6)]" data-testid="film-chapter">{scene.chapter}</div>

        {/* Fermer */}
        <button onClick={onClose} data-testid="film-close" className="absolute top-3 right-3 z-40 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"><X className="w-5 h-5" /></button>

        {/* Sous-titre */}
        <div className="absolute bottom-20 left-0 right-0 z-30 px-6 text-center">
          <AnimatePresence mode="wait">
            <motion.p key={idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-white text-sm sm:text-lg font-semibold max-w-2xl mx-auto bg-black/70 backdrop-blur px-5 py-2.5 rounded-xl [text-shadow:_0_1px_6px_rgba(0,0,0,0.7)]" data-testid="film-subtitle">{scene.sub}</motion.p>
          </AnimatePresence>
        </div>

        {/* Fin : CTA */}
        <AnimatePresence>
          {ended && (
            <motion.div {...fade} className="absolute inset-0 z-40 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6" data-testid="film-end">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C19A6B] font-semibold mb-2">Mon Agent IA · Off Markette</p>
              <p className="text-white text-2xl sm:text-3xl font-extrabold max-w-lg leading-tight">Votre force de prospection supplémentaire.</p>
              <p className="calli text-2xl text-[#C19A6B] mt-3">Les portails attendent les acquéreurs. Nous allons les chercher.</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7">
                <button onClick={() => { onActivate && onActivate(); }} data-testid="film-activate" className="inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#A8865C] text-white font-semibold py-3 px-8 rounded-xl transition-colors"><Zap className="w-4 h-4" /> Activer mon Agent IA</button>
                <button onClick={restart} data-testid="film-replay" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold py-3 px-8 rounded-xl transition-colors"><RotateCcw className="w-4 h-4" /> Revoir le film</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Barre de contrôle */}
      <div className="relative z-30 bg-[#0A0A0A] border-t border-white/10 px-4 py-3">
        <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden mb-2.5" data-testid="film-progress"><motion.div className="h-full bg-[#C19A6B]" style={{ width: `${(t / TOTAL) * 100}%` }} /></div>
        <div className="flex items-center gap-3">
          <button onClick={() => (ended ? restart() : setPlaying((p) => !p))} data-testid="film-playpause" className="w-9 h-9 rounded-full bg-[#C19A6B] flex items-center justify-center text-white shrink-0">{ended ? <RotateCcw className="w-4 h-4" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
          <span className="text-[11px] text-zinc-300 font-mono tabular-nums">{Math.floor(t)}s / {TOTAL}s</span>
          <div className="ml-auto flex items-center gap-2 relative">
            {music && <button onClick={() => setMuted((m) => !m)} data-testid="film-mute" className="text-zinc-300 hover:text-white">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>}
            <button onClick={() => setShowMusic((s) => !s)} data-testid="film-music-btn" className="inline-flex items-center gap-1.5 text-[11px] text-white bg-white/10 hover:bg-white/20 border border-white/25 rounded-lg px-2.5 py-1.5 transition-colors"><Music className="w-3.5 h-3.5 text-[#E4C79E]" /> {music ? "Ma musique" : "Ajouter ma musique"}</button>
            {showMusic && (
              <div className="absolute bottom-10 right-0 w-72 bg-[#141210] border border-white/15 rounded-xl p-3 shadow-2xl" data-testid="film-music-panel">
                <p className="text-[11px] text-zinc-300 mb-2">Collez l'URL de votre piste audio (.mp3) — elle jouera avec le film.</p>
                <input defaultValue={music} onKeyDown={(e) => { if (e.key === "Enter") { saveMusic(e.target.value.trim()); setShowMusic(false); } }} placeholder="https://…/musique.mp3" className="w-full text-xs bg-white/5 border border-white/15 rounded-lg px-2.5 py-2 text-white outline-none focus:border-[#C19A6B]" data-testid="film-music-input" />
                <div className="flex gap-2 mt-2">
                  <button onClick={(e) => { const inp = e.currentTarget.parentElement.previousElementSibling; saveMusic(inp.value.trim()); setShowMusic(false); }} className="flex-1 text-[11px] font-semibold bg-[#C19A6B] text-white rounded-lg py-1.5">Utiliser</button>
                  {music && <button onClick={() => { saveMusic(""); }} className="text-[11px] text-zinc-400 hover:text-white px-2">Retirer</button>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {music && <audio ref={audioRef} src={music} loop />}
    </motion.div>,
    document.body
  );
}

// ---------- Boucle Hero silencieuse (~14s) ----------
const LOOP = [
  { dur: 2.6, render: () => <EmmaActive /> },
  { dur: 2.4, render: () => <RadarShot /> },
  { dur: 2.2, render: () => <MatchShot /> },
  { dur: 2.4, render: () => <MiniFiche /> },
  { dur: 2.4, render: () => <WhatsAppShot /> },
  { dur: 2.2, render: () => <div className="text-center"><motion.div {...rise} className="inline-flex items-center gap-2 bg-white rounded-xl border border-[#C19A6B]/30 px-4 py-3 shadow-xl"><Calendar className="w-5 h-5 text-[#C19A6B]" /><span className="text-sm font-bold text-[#111827]">Visite demandée ✓</span></motion.div></div> },
];
const LOOP_TOTAL = LOOP.reduce((a, s) => a + s.dur, 0);
const FILM_MM = Math.floor(TOTAL / 60), FILM_SS = TOTAL % 60;
const FILM_LABEL = `≈ ${FILM_MM} min ${FILM_SS.toString().padStart(2, "0")}`;
const HERO_TAGS = ["Visite IA", "Home Staging", "Matching acquéreurs", "Emma IA"];

export function HeroFilmLoop({ onPlay }) {
  const [t, setT] = useState(0);
  const raf = useRef(null); const last = useRef(null);
  useEffect(() => {
    const loop = (ts) => { if (last.current == null) last.current = ts; const dt = (ts - last.current) / 1000; last.current = ts; setT((p) => (p + dt) % LOOP_TOTAL); raf.current = requestAnimationFrame(loop); };
    raf.current = requestAnimationFrame(loop); return () => cancelAnimationFrame(raf.current);
  }, []);
  let acc = 0, idx = 0;
  for (let i = 0; i < LOOP.length; i++) { if (t < acc + LOOP[i].dur) { idx = i; break; } acc += LOOP[i].dur; idx = i; }
  return (
    <div className="relative w-full" data-testid="hero-film-loop">
      {/* halo subtil */}
      <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-[#C19A6B]/25 via-[#C19A6B]/5 to-transparent blur-3xl pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        whileHover={{ y: -5 }}
        className="group relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_40px_90px_-24px_rgba(0,0,0,0.65)] bg-gradient-to-br from-[#0c0b0a] to-[#171310] cursor-pointer"
        onClick={onPlay} data-testid="hero-film-play">
        {/* stage 16:9 */}
        <div className="relative aspect-video flex items-center justify-center p-5 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={idx} {...fade} transition={{ duration: 0.5 }} className="w-full flex items-center justify-center scale-[0.82] sm:scale-90 lg:scale-100 origin-center">
              {LOOP[idx].render()}
            </motion.div>
          </AnimatePresence>

          {/* live badge */}
          <div className="absolute top-3.5 left-3.5 inline-flex items-center gap-1.5 bg-black/45 backdrop-blur px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /><span className="text-[10px] text-zinc-100 font-medium tracking-wide">Emma · en direct</span>
          </div>
          {/* durée */}
          <div className="absolute top-3.5 right-3.5 text-[10px] text-zinc-300 bg-black/40 backdrop-blur px-2 py-1 rounded-full">Film démo · {FILM_LABEL}</div>

          {/* bouton play central */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.span whileHover={{ scale: 1.06 }} className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-white/12 backdrop-blur-md border border-white/40 flex items-center justify-center shadow-2xl transition-colors group-hover:bg-[#C19A6B] group-hover:border-[#C19A6B]">
              <Play className="w-6 h-6 sm:w-7 sm:h-7 text-white fill-white ml-0.5" />
            </motion.span>
          </div>
        </div>

        {/* barre basse : tags + progression */}
        <div className="absolute bottom-0 inset-x-0 p-3 sm:p-4 bg-gradient-to-t from-black/75 via-black/35 to-transparent">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {HERO_TAGS.map((tg) => (
              <span key={tg} className="text-[9px] sm:text-[10px] tracking-wide text-zinc-100 bg-white/10 border border-white/15 rounded-full px-2 py-0.5">{tg}</span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="h-0.5 flex-1 bg-white/15 rounded-full overflow-hidden"><div className="h-full bg-[#C19A6B]" style={{ width: `${(t / LOOP_TOTAL) * 100}%` }} /></div>
            <span className="text-[10px] text-zinc-300 inline-flex items-center gap-1"><Play className="w-3 h-3 fill-zinc-300" /> Voir le film</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
